import React, { useState, useEffect, useRef } from 'react';
import '../BookList.css';

/**
 * Reusable filter dropdown used across Librarian list pages.
 *
 * Replaces the native <select> for filters so the menu stays aligned and
 * contained within its card on mobile (native <select> popups can overflow
 * the card in narrow/emulated viewports). Reuses the responsive-aware
 * `.book-filter-dropdown-*` styles from BookList.css.
 *
 * Props:
 *  - value:        currently selected value ('' means the placeholder option)
 *  - options:      array of strings, or array of { value, label }
 *  - placeholder:  label shown when nothing is selected
 *  - onChange:     (value) => void
 *  - className:    extra class(es) for the wrapper (e.g. "lib-select-block"
 *                  to make it fill its container instead of the compact
 *                  filter-row sizing)
 *  - triggerClassName: extra class(es) for the trigger button (e.g. "ab-input"
 *                  so it matches sibling form fields)
 */
const FilterSelect = ({
  value,
  options = [],
  placeholder = 'Select',
  onChange,
  className = '',
  triggerClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const norm = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const selected = norm.find((o) => String(o.value) === String(value));

  return (
    <div className={`book-filter-dropdown-wrap ${className}`} ref={ref}>
      <button
        type="button"
        className={`book-filter-dropdown-btn ${triggerClassName} ${value ? 'active' : ''}`}
        onClick={() => setOpen((p) => !p)}
      >
        <span className="book-filter-dropdown-label">{selected ? selected.label : placeholder}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
      </button>

      {open && (
        <div className="book-filter-dropdown-menu">
          <div className="book-filter-dropdown-list">
            {norm.map((o) => (
              <div
                key={String(o.value)}
                className={`book-filter-dropdown-item ${String(o.value) === String(value) ? 'selected' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSelect;
